
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

interface INormalizedSelectItem {
  value: string;
  text: string;
}

/**
 * Native <select> based dropdown.
 *
 * v-select renders a role="button" <div> that wraps a readonly <input>, which
 * VoiceOver does not announce at all and NVDA only reads while the list is
 * open (WCAG 1.3.1, 4.1.2). A native <select> with a real <label for> and an
 * error message tied to it with aria-describedby is announced correctly by all
 * screen readers.
 */
@Component({
  name: "AccessibleSelect",
})
export default class AccessibleSelect extends Vue {
  @Prop({ default: "" })
  value!: string;

  /** Either plain strings or { value, text } objects. */
  @Prop({ default: () => [] })
  items!: (string | INormalizedSelectItem)[];

  @Prop({ default: "" })
  label!: string;

  @Prop({ default: false })
  required!: boolean;

  @Prop({ default: false })
  disabled!: boolean;

  /** Text of the empty option. Defaults to general.choose_option. */
  @Prop({ default: "" })
  placeholder!: string;

  @Prop({ default: "" })
  id!: string;

  @Prop({ default: "" })
  name!: string;

  /** Error set by the parent. When given, it is always shown. */
  @Prop({ default: "" })
  errorMessage!: string;

  uid: string = (Math.random() * 10e16).toString(16);

  /** Set by validate(), i.e. when the user tries to submit without a choice. */
  validationRequested: boolean = false;

  get selectId(): string {
    return this.id || `a11y-select-${this.uid}`;
  }

  get messageId(): string {
    return `${this.selectId}-message`;
  }

  get normalizedItems(): INormalizedSelectItem[] {
    return this.items.map((item) =>
      typeof item === "string" ? { value: item, text: item } : item
    );
  }

  get placeholderText(): string {
    return this.placeholder || (this.$t("general.choose_option") as string);
  }

  get isEmpty(): boolean {
    return !this.value || !this.value.trim();
  }

  get hasError(): boolean {
    if (this.errorMessage) {
      return true;
    }
    return this.required && this.isEmpty && this.validationRequested;
  }

  get errorText(): string {
    return this.errorMessage || (this.$t("error.must_choose_option") as string);
  }

  @Watch("value")
  valueChanged() {
    if (!this.isEmpty) {
      this.validationRequested = false;
    }
  }

  onChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.$emit("input", value);
    this.$emit("change", value);
  }

  focus() {
    (this.$refs.selectRef as HTMLSelectElement | undefined)?.focus();
  }

  /**
   * Called by the parent when the form is submitted. Shows the error message
   * and moves focus to the field instead of leaving the submit button
   * disabled, so keyboard and screen reader users are not stuck.
   */
  validate(): boolean {
    this.validationRequested = true;
    if (this.required && this.isEmpty) {
      this.$nextTick(() => this.focus());
      return false;
    }
    return true;
  }
}
